import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const emptyForm = {
  number: '',
  rule: '',
  active: true,
};

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('rules')
      .select('*')
      .order('number', { ascending: true });

    if (!error) setRules(data);
    setLoading(false);
  };

  const handleEdit = (rule) => {
    setForm({
      number: rule.number,
      rule: rule.rule,
      active: rule.active,
    });
    setEditingId(rule.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await supabase.from('rules').delete().eq('id', id);
    fetchRules();
  };

  const handleToggleActive = async (rule) => {
    await supabase
      .from('rules')
      .update({ active: !rule.active })
      .eq('id', rule.id);
    fetchRules();
  };

  const handleMoveUp = async (rule, index) => {
    if (index === 0) return;
    const above = rules[index - 1];
    await supabase.from('rules').update({ number: above.number }).eq('id', rule.id);
    await supabase.from('rules').update({ number: rule.number }).eq('id', above.id);
    fetchRules();
  };

  const handleMoveDown = async (rule, index) => {
    if (index === rules.length - 1) return;
    const below = rules[index + 1];
    await supabase.from('rules').update({ number: below.number }).eq('id', rule.id);
    await supabase.from('rules').update({ number: rule.number }).eq('id', below.id);
    fetchRules();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rule) {
      alert('Please enter rule text.');
      return;
    }

    setSaving(true);

    if (editingId) {
      await supabase
        .from('rules')
        .update({
          number: parseInt(form.number),
          rule: form.rule,
          active: form.active,
        })
        .eq('id', editingId);
      setSuccessMsg('Rule updated successfully!');
    } else {
      // Auto-assign next number if not specified
      const nextNumber = form.number
        ? parseInt(form.number)
        : rules.length + 1;

      await supabase
        .from('rules')
        .insert({
          number: nextNumber,
          rule: form.rule,
          active: form.active,
        });
      setSuccessMsg('Rule added successfully!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchRules();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Program Rules
          <span style={styles.topbarCount}>{rules.length} rules</span>
        </h1>
        <button
          style={styles.topbarBtn}
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setForm(emptyForm);
          }}
        >
          {showForm && !editingId ? '✕ Cancel' : '+ Add Rule'}
        </button>
      </div>

      <div style={styles.content}>

        {/* SUCCESS */}
        {successMsg && (
          <div style={styles.successBanner}>✅ {successMsg}</div>
        )}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              {editingId ? 'Edit Rule' : 'Add New Rule'}
            </h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>RULE NUMBER</label>
                  <input
                    style={styles.input}
                    type="number"
                    placeholder={`e.g. ${rules.length + 1}`}
                    value={form.number}
                    onChange={(e) => setForm({ ...form, number: e.target.value })}
                    min="1"
                  />
                  <div style={styles.fieldHint}>
                    Leave blank to add at the end
                  </div>
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>STATUS</label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Active — visible in app
                  </label>
                </div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>RULE TEXT *</label>
                <textarea
                  style={styles.textarea}
                  placeholder="e.g. The Gold Card may be used for food & beverage purchases only."
                  value={form.rule}
                  onChange={(e) => setForm({ ...form, rule: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              {/* Preview */}
              {form.rule && (
                <div style={styles.previewWrap}>
                  <div style={styles.previewLabel}>PREVIEW</div>
                  <div style={styles.previewCard}>
                    <div style={styles.previewNum}>
                      {form.number || rules.length + 1}
                    </div>
                    <div style={styles.previewText}>{form.rule}</div>
                  </div>
                </div>
              )}

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Rule'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        )}

        {/* RULES LIST */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>All Rules</h2>
          <p style={styles.sectionHint}>
            Use ↑ ↓ arrows to reorder rules
          </p>
        </div>

        <div style={styles.rulesList}>
          {rules.length === 0 ? (
            <div style={styles.empty}>
              No rules yet. Click "+ Add Rule" to create one.
            </div>
          ) : (
            rules.map((rule, index) => (
              <div
                key={rule.id}
                style={{
                  ...styles.ruleRow,
                  ...(rule.active ? {} : styles.ruleRowInactive),
                }}
              >
                {/* Number */}
                <div style={styles.ruleNum}>{rule.number}</div>

                {/* Text */}
                <div style={styles.ruleText}>{rule.rule}</div>

                {/* Actions */}
                <div style={styles.ruleActions}>
                  {/* Reorder buttons */}
                  <div style={styles.reorderBtns}>
                    <button
                      style={styles.arrowBtn}
                      onClick={() => handleMoveUp(rule, index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      style={styles.arrowBtn}
                      onClick={() => handleMoveDown(rule, index)}
                      disabled={index === rules.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>

                  {/* Active toggle */}
                  <Toggle
                    value={rule.active}
                    onChange={() => handleToggleActive(rule)}
                  />

                  {/* Edit / Delete */}
                  <button
                    style={styles.editBtn}
                    onClick={() => handleEdit(rule)}
                  >
                    Edit
                  </button>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDelete(rule.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      title={value ? 'Active — click to hide' : 'Inactive — click to show'}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        background: value ? GOLD : '#ddd',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        top: '3px',
        left: value ? '19px' : '3px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarCount: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#aaa',
  },
  topbarBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  content: { padding: '24px', flex: 1 },
  successBanner: {
    background: '#f0fff4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: '#166534',
    fontSize: '14px',
  },
  formCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '14px',
    padding: '24px',
    marginBottom: '24px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '4px',
  },
  formRow: { marginBottom: '16px' },
  label: {
    display: 'block',
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  fieldHint: {
    color: '#bbb',
    fontSize: '11px',
    marginTop: '4px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#444',
    cursor: 'pointer',
    marginTop: '8px',
  },
  previewWrap: { marginBottom: '20px' },
  previewLabel: {
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '8px',
  },
  previewCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${GOLD}`,
    borderRadius: '0 12px 12px 0',
    padding: '14px 16px',
    maxWidth: '600px',
  },
  previewNum: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#111',
    color: GOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  previewText: {
    fontSize: '14px',
    color: '#222',
    lineHeight: '1.6',
    flex: 1,
  },
  formBtns: {
    display: 'flex',
    gap: '12px',
  },
  submitBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none',
    color: '#888',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
  },
  sectionHint: {
    fontSize: '12px',
    color: '#aaa',
    margin: 0,
  },
  rulesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  ruleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${GOLD}`,
    borderRadius: '0 12px 12px 0',
    padding: '14px 16px',
  },
  ruleRowInactive: {
    opacity: 0.5,
    borderLeftColor: '#ddd',
  },
  ruleNum: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#111',
    color: GOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  ruleText: {
    flex: 1,
    fontSize: '14px',
    color: '#222',
    lineHeight: '1.6',
  },
  ruleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  reorderBtns: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  arrowBtn: {
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    width: '24px',
    height: '20px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    color: '#666',
  },
  editBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#444',
    cursor: 'pointer',
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
  },
};